import React, { ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  children,
  className = "",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-btn font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-pink/50 disabled:opacity-50 disabled:cursor-not-allowed";

  const variantClasses = {
    primary:
      "bg-neon-gradient font-bold text-ink shadow-neon-pink hover:opacity-90",
    secondary:
      "bg-white/5 border border-white/10 text-fg-card hover:bg-white/10",
    outline:
      "bg-transparent border border-white/[.14] text-fg-card hover:bg-white/5",
    ghost: "bg-transparent text-neon-cyan hover:opacity-80",
    danger:
      "bg-transparent border border-neon-pink/40 text-neon-pink-soft hover:bg-neon-pink/10",
  };

  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-6 py-3.5 text-base",
  };

  const iconSizeClasses = {
    sm: "h-4 w-4",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2
          className={`animate-spin -ml-1 mr-2 ${iconSizeClasses[size]}`}
        />
      )}
      {children}
    </button>
  );
};

export default Button;
